import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Award, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT = {
  hero_headline: 'Learn to Save Lives',
  hero_subheadline: 'Prepare Until Success Happens',
  hero_description: 'Professional CPR and First Aid training for healthcare providers, educators, businesses, and everyone who wants to be prepared.',
  why_title: 'Why Choose PUSH Training - NC?',
  why_subtitle: "We're committed to providing top-quality, hands-on training that prepares you for real-life emergencies.",
  feature_1_title: 'Certified Instructors', feature_1_desc: 'Learn from experienced, AHA-certified professionals dedicated to your success.',
  feature_2_title: 'Hands-On Training', feature_2_desc: 'Practice real-world scenarios with state-of-the-art equipment and mannequins.',
  feature_3_title: 'Flexible Scheduling', feature_3_desc: 'Choose from multiple class times or request on-site training for your group.',
  feature_4_title: 'Community Focused', feature_4_desc: 'Serving Henderson and Durham, NC with a commitment to community safety.',
  cta_headline: 'Ready to Make a Difference?',
  cta_description: 'Join hundreds of individuals who have learned life-saving skills with PUSH Training - NC.'
};

const ICONS = [Award, Users, Clock, Heart];

export const Home = () => {
  const [classes, setClasses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [content, setContent] = useState(DEFAULT);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/classes`),
      axios.get(`${API}/testimonials`),
      axios.get(`${API}/content/home`)
    ]).then(([c, t, ct]) => {
      setClasses(c.data);
      setTestimonials(t.data);
      setContent({ ...DEFAULT, ...ct.data });
    }).catch(console.error);
  }, []);

  const availableClasses = classes.filter(c => c.available);

  const features = [1,2,3,4].map((n, i) => ({
    title: content[`feature_${n}_title`],
    desc: content[`feature_${n}_desc`],
    Icon: ICONS[i]
  }));

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-br from-red-50 via-white to-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {content.hero_headline.includes('Save Lives')
                ? <>{content.hero_headline.split('Save Lives')[0]}<span className="text-red-600">Save Lives</span></>
                : content.hero_headline}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-4 font-medium">{content.hero_subheadline}</p>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">{content.hero_description}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://zbooking.us/xu8Qz" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6">
                  Book a Class Now <ArrowRight className="ml-2" size={20} />
                </Button>
              </a>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-2 border-red-600 text-red-600 hover:bg-red-50 text-lg px-8 py-6">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{content.why_title}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{content.why_subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map(({ title, desc, Icon }) => (
              <Card key={title} className="border-2 hover:border-red-200 transition-all hover:shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-red-600" size={24} />
                  </div>
                  <CardTitle className="text-xl">{title}</CardTitle>
                </CardHeader>
                <CardContent><CardDescription className="text-base">{desc}</CardDescription></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Available Classes</h2>
            <p className="text-lg text-gray-600">Browse our current offerings and book your spot today</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {availableClasses.map((course) => (
              <Card key={course.id} className="border-2 hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-2xl">{course.name}</CardTitle>
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">Available</span>
                  </div>
                  <CardDescription className="text-base">{course.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between"><span className="text-gray-600">Duration:</span><span className="font-semibold">{course.duration}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Price:</span><span className="text-2xl font-bold text-red-600">${course.price}</span></div>
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white" onClick={() => window.open(course.booking_url || 'https://zbooking.us/xu8Qz', '_blank')}>
                      Book This Class
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/services">
              <Button variant="outline" size="lg" className="border-2 border-red-600 text-red-600 hover:bg-red-50">
                View All Services <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Students Say</h2>
              <p className="text-lg text-gray-600">Real feedback from real people we've trained</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((t) => (
                <Card key={t.id} className="border-2">
                  <CardHeader>
                    <div className="flex gap-1 mb-2">{[...Array(t.rating)].map((_, i) => <Heart key={i} size={18} className="text-red-600" fill="currentColor" />)}</div>
                    <CardDescription className="text-base text-gray-700 italic">"{t.content}"</CardDescription>
                  </CardHeader>
                  <CardContent><p className="font-semibold text-gray-900">{t.name}</p><p className="text-sm text-gray-600">{t.role}</p></CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{content.cta_headline}</h2>
          <p className="text-xl text-red-100 mb-8">{content.cta_description}</p>
          <a href="https://zbooking.us/xu8Qz" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6">
              Book Your Class Today <ArrowRight className="ml-2" size={20} />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};
